import { useAgentGatewayStats } from "@/hooks/useAgentGateway";
import { cn } from "@/lib/utils";

/**
 * Live Total / Active / Inactive from agent_gateway getStats.
 * Never falls back to mock fixture counts.
 */
export function AgentLayerCounts({
	className,
	compact = false,
}: {
	className?: string;
	/** Single-line shorter labels for tight header bands */
	compact?: boolean;
}) {
	const { data: stats, isLoading, isError } = useAgentGatewayStats();

	if (isLoading) {
		return (
			<span
				className={cn(
					"font-mono text-[10px] text-yellow-400/60 sm:text-[11px]",
					className,
				)}
			>
				…
			</span>
		);
	}

	if (isError || !stats) {
		return (
			<span
				className={cn(
					"font-mono text-[10px] text-yellow-400/60 sm:text-[11px]",
					className,
				)}
			>
				—
			</span>
		);
	}

	const total = Number(stats.agentCount);
	const active = Number(stats.activeCount);
	const inactive = Math.max(0, total - active);

	if (compact) {
		return (
			<span
				className={cn(
					"font-mono text-[10px] font-semibold text-yellow-400 sm:text-[11px]",
					className,
				)}
				title={`Total ${total} · Active ${active} · Inactive ${inactive}`}
			>
				<span className="rounded border border-yellow-500/40 bg-yellow-400/10 px-1.5">
					{total}
				</span>
				<span className="ml-1 hidden text-yellow-400/70 sm:inline">
					total · {active} active · {inactive} inactive
				</span>
			</span>
		);
	}

	return (
		<span
			className={cn(
				"inline-flex flex-wrap items-center gap-x-2 gap-y-0.5 font-mono text-[10px] font-semibold text-yellow-400 sm:text-[11px]",
				className,
			)}
		>
			<span className="rounded border border-yellow-500/50 bg-yellow-400/10 px-2 py-0.5">
				Total {total}
			</span>
			<span className="rounded border border-yellow-500/40 bg-yellow-400/5 px-2 py-0.5 text-yellow-400/90">
				Active {active}
			</span>
			<span className="rounded border border-yellow-500/40 bg-yellow-400/5 px-2 py-0.5 text-yellow-400/90">
				Inactive {inactive}
			</span>
		</span>
	);
}
